import React, { useEffect, useMemo, useState } from 'react';
import DiscussionCard from '../components/DiscussionCard';
import { discussionService } from '../services/api';
import { Discussion } from '../types';

const ArchivePage: React.FC = () => {
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [hazardFilter, setHazardFilter] = useState<string>('all');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      discussionService.getAll('published', 100, 0),
      discussionService.getAll('archived', 100, 0),
    ])
      .then(([publishedResponse, archivedResponse]) => {
        const merged = [...publishedResponse.data, ...archivedResponse.data];
        const unique = Array.from(new Map(merged.map((discussion) => [discussion.id, discussion])).values());
        unique.sort((left, right) => {
          const leftTime = new Date(left.archived_at || left.published_at || left.created_at).getTime();
          const rightTime = new Date(right.archived_at || right.published_at || right.created_at).getTime();
          return rightTime - leftTime;
        });
        setDiscussions(unique);
      })
      .catch(() => setError('Failed to load archive'))
      .finally(() => setLoading(false));
  }, []);

  const hazardOptions = useMemo(() => {
    const options = new Set<string>();
    discussions.forEach((discussion) => {
      (discussion.hazard_tags || []).forEach((hazard) => options.add(hazard));
    });
    return ['all', ...Array.from(options).sort()];
  }, [discussions]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return discussions.filter((discussion) => {
      const matchesHazard = hazardFilter === 'all' || (discussion.hazard_tags || []).includes(hazardFilter);
      if (!matchesHazard) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const haystack = [
        discussion.title,
        discussion.summary || discussion.discussion_body || '',
        discussion.tags.join(' '),
        (discussion.hazard_tags || []).join(' '),
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [discussions, hazardFilter, query]);

  const totalViews = useMemo(
    () => discussions.reduce((sum, discussion) => sum + discussion.views, 0),
    [discussions],
  );

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Public archive</p>
            <h1 className="page-heading">Published discussion archive</h1>
            <p className="page-copy">
              Browse current and auto-archived discussions, filter by hazard focus, and review MD validity windows in both Z and your local device time.
            </p>
          </div>
        </div>

        <div className="dashboard-grid archive-summary-grid">
          <div className="module-card">
            <p className="eyebrow">Products</p>
            <h3 className="metric-value">{discussions.length}</h3>
            <p className="module-note">Published and archived discussions loaded</p>
          </div>
          <div className="module-card">
            <p className="eyebrow">Hazards</p>
            <h3 className="metric-value">{Math.max(hazardOptions.length - 1, 0)}</h3>
            <p className="module-note">Unique hazard tags in the current archive slice</p>
          </div>
          <div className="module-card">
            <p className="eyebrow">Reach</p>
            <h3 className="metric-value">{totalViews}</h3>
            <p className="module-note">Total recorded archive views</p>
          </div>
        </div>

        <div className="card filter-card">
          <div className="form-grid two-column">
            <div className="form-group">
              <label className="form-label" htmlFor="archive-query">Search archive</label>
              <input
                id="archive-query"
                className="form-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search title, tags, hazards, or body text"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="hazard-filter">Hazard focus</label>
              <select
                id="hazard-filter"
                className="form-input"
                value={hazardFilter}
                onChange={(event) => setHazardFilter(event.target.value)}
              >
                {hazardOptions.map((hazard) => (
                  <option key={hazard} value={hazard}>{hazard === 'all' ? 'All hazards' : hazard}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading && <div className="card">Loading archive...</div>}
        {error && <div className="alert alert-error">{error}</div>}
        {!loading && !error && filtered.length === 0 && <div className="card">No published discussions match this filter.</div>}
        {!loading && !error && filtered.length > 0 && (
          <div className="discussion-stack">
            {filtered.map((discussion) => (
              <DiscussionCard key={discussion.id} discussion={discussion} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchivePage;